import { lazy, type ComponentType } from 'react';
import type { ConfigRendererProps } from './types';
import { StandardConfigRenderer } from './StandardConfigRenderer';

export type { ConfigRendererProps, StepNodeData, StepNode } from './types';

// Every renderer below is loaded on demand. Importing this module used to pull
// in all ~105 renderers (and their ace-editor / dnd-kit / schema-dialog
// dependencies) even though a step panel renders exactly one, which is what put
// several MB of JS on the workflow overview and editor routes. Vite emits one
// small chunk per renderer instead; `getConfigRenderer` keeps its signature and
// callers wrap the result in <Suspense>.
//
// `StandardConfigRenderer` stays eager: it is the fallback for unknown step
// types and is small, so the common path avoids a suspense flash.

// Each renderer is both a lazy component and a prefetchable loader: the editor
// warms the chunk for a step type on hover so opening the panel is instant.
// Repeat calls are free — the module registry caches the dynamic import.
const rendererLoaders = new WeakMap<
  ComponentType<ConfigRendererProps>,
  () => Promise<unknown>
>();

function lazyRenderer(
  load: () => Promise<{ default: ComponentType<ConfigRendererProps> }>,
): ComponentType<ConfigRendererProps> {
  const component = lazy(load);
  rendererLoaders.set(component, load);
  return component;
}

const UpsertSupplierWarehouseConstraintConfigRenderer = lazyRenderer(
  async () => ({
    default: (await import('./UpsertSupplierWarehouseConstraintConfigRenderer'))
      .UpsertSupplierWarehouseConstraintConfigRenderer,
  }),
);
const SpaRenewalConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SpaRenewalConfigRenderer'))
    .SpaRenewalConfigRenderer,
}));
const RunWorkbookConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./RunWorkbookConfigRenderer'))
    .RunWorkbookConfigRenderer,
}));
const ApprovalConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ApprovalConfigRenderer')).ApprovalConfigRenderer,
}));
const SubmitDraftSalesOrdersConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SubmitDraftSalesOrdersConfigRenderer'))
    .SubmitDraftSalesOrdersConfigRenderer,
}));
const EmailRuleTriggerConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./email-rule-trigger/EmailRuleTriggerConfigRenderer'))
    .EmailRuleTriggerConfigRenderer,
}));
const EventTriggerConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./event-trigger/EventTriggerConfigRenderer'))
    .EventTriggerConfigRenderer,
}));
const ExtractDocumentContentConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ExtractDocumentContentConfigRenderer'))
    .ExtractDocumentContentConfigRenderer,
}));
const SchedulePaymentConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SchedulePaymentConfigRenderer'))
    .SchedulePaymentConfigRenderer,
}));
const ScheduleTriggerConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ScheduleTriggerConfigRenderer'))
    .ScheduleTriggerConfigRenderer,
}));
const HttpTriggerConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./http-trigger/HttpTriggerConfigRenderer'))
    .HttpTriggerConfigRenderer,
}));
const FormTriggerConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./form-trigger/FormTriggerConfigRenderer'))
    .FormTriggerConfigRenderer,
}));
const FetchPurchaseOrderFromFamousConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./FetchPurchaseOrderFromFamousConfigRenderer'))
    .FetchPurchaseOrderFromFamousConfigRenderer,
}));
const ClassifyDocumentConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ClassifyDocumentConfigRenderer'))
    .ClassifyDocumentConfigRenderer,
}));
const ProcessPickTicketBOLConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ProcessPickTicketBOLConfigRenderer'))
    .ProcessPickTicketBOLConfigRenderer,
}));
const ExtractFamousOrderFromEmailSubjectConfigRenderer = lazyRenderer(
  async () => ({
    default: (
      await import('./ExtractFamousOrderFromEmailSubjectConfigRenderer')
    ).ExtractFamousOrderFromEmailSubjectConfigRenderer,
  }),
);
const BlobStorageFetchConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./BlobStorageFetchConfigRenderer'))
    .BlobStorageFetchConfigRenderer,
}));
const ParseFileConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ParseFileConfigRenderer')).ParseFileConfigRenderer,
}));
const ParseEdiConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ParseEdiConfigRenderer')).ParseEdiConfigRenderer,
}));
const PipelineRowsInsertConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./PipelineRowsInsertConfigRenderer'))
    .PipelineRowsInsertConfigRenderer,
}));
const PipelineRowsProcessConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./PipelineRowsProcessConfigRenderer'))
    .PipelineRowsProcessConfigRenderer,
}));
const PipelineRowsQueryConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./PipelineRowsQueryConfigRenderer'))
    .PipelineRowsQueryConfigRenderer,
}));
const PipelineRowsUpdateConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./PipelineRowsUpdateConfigRenderer'))
    .PipelineRowsUpdateConfigRenderer,
}));
const BackgroundAgentConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./background-agent/BackgroundAgentConfigRenderer'))
    .BackgroundAgentConfigRenderer,
}));
const RunRebateForecastConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./RunRebateForecastConfigRenderer'))
    .RunRebateForecastConfigRenderer,
}));
const RunDemandForecastConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./RunDemandForecastConfigRenderer'))
    .RunDemandForecastConfigRenderer,
}));
const CreateTaskConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./tasks/CreateTaskConfigRenderer'))
    .CreateTaskConfigRenderer,
}));
const CreateTypedTaskConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./tasks/CreateTypedTaskConfigRenderer'))
    .CreateTypedTaskConfigRenderer,
}));
const GenerateTaskConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./tasks/GenerateTaskConfigRenderer'))
    .GenerateTaskConfigRenderer,
}));
const ReassignTaskConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./tasks/ReassignTaskConfigRenderer'))
    .ReassignTaskConfigRenderer,
}));
const ResolveTaskConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./tasks/ResolveTaskConfigRenderer'))
    .ResolveTaskConfigRenderer,
}));
const CreateSubtaskConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./tasks/CreateSubtaskConfigRenderer'))
    .CreateSubtaskConfigRenderer,
}));
const CompleteTaskConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./tasks/CompleteTaskConfigRenderer'))
    .CompleteTaskConfigRenderer,
}));
const SyncTasksFromRowsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./tasks/SyncTasksFromRowsConfigRenderer'))
    .SyncTasksFromRowsConfigRenderer,
}));
const SplitDocumentsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SplitDocumentsConfigRenderer'))
    .SplitDocumentsConfigRenderer,
}));
const FilterEmailConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./FilterEmailConfigRenderer'))
    .FilterEmailConfigRenderer,
}));
const FilterEmailByCustomerConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./FilterEmailByCustomerConfigRenderer'))
    .FilterEmailByCustomerConfigRenderer,
}));
const ProcessSDConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ProcessSDConfigRenderer')).ProcessSDConfigRenderer,
}));
const IdentifySDCustomerConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./IdentifySDCustomerConfigRenderer'))
    .IdentifySDCustomerConfigRenderer,
}));
const IdentifyAndFilterSDConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./IdentifyAndFilterSDConfigRenderer'))
    .IdentifyAndFilterSDConfigRenderer,
}));
const IdentifyCustomerConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./IdentifyCustomerConfigRenderer'))
    .IdentifyCustomerConfigRenderer,
}));
const IdentifySupplierConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./IdentifySupplierConfigRenderer'))
    .IdentifySupplierConfigRenderer,
}));
const IdentifyLocationConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./IdentifyLocationConfigRenderer'))
    .IdentifyLocationConfigRenderer,
}));
const MatchInvoiceLineItemsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./MatchInvoiceLineItemsConfigRenderer'))
    .MatchInvoiceLineItemsConfigRenderer,
}));
const MatchSupplierDocumentLinesConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./MatchSupplierDocumentLinesConfigRenderer'))
    .MatchSupplierDocumentLinesConfigRenderer,
}));
const WriteSupplierProductMatchesConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./WriteSupplierProductMatchesConfigRenderer'))
    .WriteSupplierProductMatchesConfigRenderer,
}));
const ResolveEntityWatchersConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ResolveEntityWatchersConfigRenderer'))
    .ResolveEntityWatchersConfigRenderer,
}));
const SendEmailConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SendEmailConfigRenderer')).SendEmailConfigRenderer,
}));
const SendAnnouncementConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SendAnnouncementConfigRenderer'))
    .SendAnnouncementConfigRenderer,
}));
const SendAnnouncementV2ConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SendAnnouncementV2ConfigRenderer'))
    .SendAnnouncementV2ConfigRenderer,
}));
const NotifyConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./NotifyConfigRenderer')).NotifyConfigRenderer,
}));
const MasterDataBulkInsertConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./MasterDataBulkInsertConfigRenderer'))
    .MasterDataBulkInsertConfigRenderer,
}));
const ClassifyMasterDataConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ClassifyMasterDataConfigRenderer'))
    .ClassifyMasterDataConfigRenderer,
}));
const TransformE844dFieldsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./TransformE844dFieldsConfigRenderer'))
    .TransformE844dFieldsConfigRenderer,
}));
const CoreEnumFanoutConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./CoreEnumFanoutConfigRenderer'))
    .CoreEnumFanoutConfigRenderer,
}));
const RandomNumberGeneratorConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./RandomNumberGeneratorConfigRenderer'))
    .RandomNumberGeneratorConfigRenderer,
}));
const DecisionConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./DecisionConfigRenderer')).DecisionConfigRenderer,
}));
const DecisionV4ConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./DecisionV4ConfigRenderer'))
    .DecisionV4ConfigRenderer,
}));
const SwitchConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SwitchConfigRenderer')).SwitchConfigRenderer,
}));
const MapValueConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./MapValueConfigRenderer')).MapValueConfigRenderer,
}));
const LogValueConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./LogValueConfigRenderer')).LogValueConfigRenderer,
}));
const ClassifyEmailConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ClassifyEmailConfigRenderer'))
    .ClassifyEmailConfigRenderer,
}));
const SplitExcelSheetsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SplitExcelSheetsConfigRenderer'))
    .SplitExcelSheetsConfigRenderer,
}));
const ClassifyDocumentContentConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ClassifyDocumentContentConfigRenderer'))
    .ClassifyDocumentContentConfigRenderer,
}));
const EntityResolverConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./EntityResolverConfigRenderer'))
    .EntityResolverConfigRenderer,
}));
const EnqueueRebateSessionConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./EnqueueRebateSessionConfigRenderer'))
    .EnqueueRebateSessionConfigRenderer,
}));
const SplitStagedPOConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SplitStagedPOConfigRenderer'))
    .SplitStagedPOConfigRenderer,
}));
const SplitDraftSalesOrderConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SplitDraftSalesOrderConfigRenderer'))
    .SplitDraftSalesOrderConfigRenderer,
}));
const CollectResultsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./CollectResultsConfigRenderer'))
    .CollectResultsConfigRenderer,
}));
const FanInConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./FanInConfigRenderer')).FanInConfigRenderer,
}));
const EmitSystemEventConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./EmitSystemEventConfigRenderer'))
    .EmitSystemEventConfigRenderer,
}));
const ClassifyOrganizationConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ClassifyOrganizationConfigRenderer'))
    .ClassifyOrganizationConfigRenderer,
}));
const ReassignDraftSalesOrderConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ReassignDraftSalesOrderConfigRenderer'))
    .ReassignDraftSalesOrderConfigRenderer,
}));
const ConvertE844sdToE844dConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ConvertE844sdToE844dConfigRenderer'))
    .ConvertE844sdToE844dConfigRenderer,
}));
const ArchiveShipAndDebitAzureFilesConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ArchiveShipAndDebitAzureFilesConfigRenderer'))
    .ArchiveShipAndDebitAzureFilesConfigRenderer,
}));
const DetectAs400ConfirmationsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./DetectAs400ConfirmationsConfigRenderer'))
    .DetectAs400ConfirmationsConfigRenderer,
}));

const CheckStorageWriteErrorsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./CheckStorageWriteErrorsConfigRenderer'))
    .CheckStorageWriteErrorsConfigRenderer,
}));
const PlatformHeartbeatConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./PlatformHeartbeatConfigRenderer'))
    .PlatformHeartbeatConfigRenderer,
}));
const DataAssetConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./DataAssetConfigRenderer')).DataAssetConfigRenderer,
}));
const StatefulTableWriteConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./StatefulTableWriteConfigRenderer'))
    .StatefulTableWriteConfigRenderer,
}));
const StatefulTableLookupRowConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./StatefulTableLookupRowConfigRenderer'))
    .StatefulTableLookupRowConfigRenderer,
}));
const UseDataMappingConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./UseDataMappingConfigRenderer'))
    .UseDataMappingConfigRenderer,
}));

const StatefulTableReadFrameConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./StatefulTableReadFrameConfigRenderer'))
    .StatefulTableReadFrameConfigRenderer,
}));
const FusionSyncConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./FusionSyncConfigRenderer'))
    .FusionSyncConfigRenderer,
}));
const CreateJiraIssueConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./CreateJiraIssueConfigRenderer'))
    .CreateJiraIssueConfigRenderer,
}));
const SendTeamsNotificationConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SendTeamsNotificationConfigRenderer'))
    .SendTeamsNotificationConfigRenderer,
}));
const RenderOrderNotificationEmailConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./RenderOrderNotificationEmailConfigRenderer'))
    .RenderOrderNotificationEmailConfigRenderer,
}));
const DetectOrderIssuesConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./DetectOrderIssuesConfigRenderer'))
    .DetectOrderIssuesConfigRenderer,
}));
const IngestArDataConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./IngestArDataConfigRenderer'))
    .IngestArDataConfigRenderer,
}));
const IngestEdiTranslationListsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./IngestEdiTranslationListsConfigRenderer'))
    .IngestEdiTranslationListsConfigRenderer,
}));
const InterpolateEmailTemplateConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./InterpolateEmailTemplateConfigRenderer'))
    .InterpolateEmailTemplateConfigRenderer,
}));
const SplitDocumentV2ConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SplitDocumentV2ConfigRenderer'))
    .SplitDocumentV2ConfigRenderer,
}));
const FilterAttachmentsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./FilterAttachmentsConfigRenderer'))
    .FilterAttachmentsConfigRenderer,
}));
const WorkflowActionConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./WorkflowActionConfigRenderer'))
    .WorkflowActionConfigRenderer,
}));
const WaitForEventConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./wait-for-event/WaitForEventConfigRenderer'))
    .WaitForEventConfigRenderer,
}));
const SnoozeWorkflowRerunConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SnoozeWorkflowRerunConfigRenderer'))
    .SnoozeWorkflowRerunConfigRenderer,
}));
const IngestMicrosoftGraphEmailConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./IngestMicrosoftGraphEmailConfigRenderer'))
    .IngestMicrosoftGraphEmailConfigRenderer,
}));
const CreateSalesOrderDraftConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./CreateSalesOrderDraftConfigRenderer'))
    .CreateSalesOrderDraftConfigRenderer,
}));
const CreateDraftSalesOrderConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./CreateDraftSalesOrderConfigRenderer'))
    .CreateDraftSalesOrderConfigRenderer,
}));
const ResolveDraftDetailsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ResolveDraftDetailsConfigRenderer'))
    .ResolveDraftDetailsConfigRenderer,
}));
const VoiceAgentConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./voice-agent/VoiceAgentConfigRenderer'))
    .VoiceAgentConfigRenderer,
}));
const CodeSandboxConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./CodeSandboxConfigRenderer'))
    .CodeSandboxConfigRenderer,
}));
const BlobStorageDataSourceAdapterConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./BlobStorageDataSourceAdapterConfigRenderer'))
    .BlobStorageDataSourceAdapterConfigRenderer,
}));
const CsvTransformConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./CsvTransformConfigRenderer'))
    .CsvTransformConfigRenderer,
}));
const CsvTransformV4ConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./CsvTransformV4ConfigRenderer'))
    .CsvTransformV4ConfigRenderer,
}));
const AiReasoningConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ai-reasoning/AiReasoningConfigRenderer'))
    .AiReasoningConfigRenderer,
}));
const VerifyStepOutputConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./verify-step-output/VerifyStepOutputConfigRenderer'))
    .VerifyStepOutputConfigRenderer,
}));
const ExternalHttpCallConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ExternalHttpCallConfigRenderer'))
    .ExternalHttpCallConfigRenderer,
}));
const WatchForFileChangesConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./WatchForFileChangesConfigRenderer'))
    .WatchForFileChangesConfigRenderer,
}));
const ObserveDataSourceLinkConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ObserveDataSourceLinkConfigRenderer'))
    .ObserveDataSourceLinkConfigRenderer,
}));
const UpsertSalesOrderLineConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./UpsertSalesOrderLineConfigRenderer'))
    .UpsertSalesOrderLineConfigRenderer,
}));

const UpsertPurchaseOrderLineConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./UpsertPurchaseOrderLineConfigRenderer'))
    .UpsertPurchaseOrderLineConfigRenderer,
}));

const FwfDataSourceWriterConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./FwfDataSourceWriterConfigRenderer'))
    .FwfDataSourceWriterConfigRenderer,
}));

const UpsertTransferOrderLineConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./UpsertTransferOrderLineConfigRenderer'))
    .UpsertTransferOrderLineConfigRenderer,
}));

const UpsertSupplierProductConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./UpsertSupplierProductConfigRenderer'))
    .UpsertSupplierProductConfigRenderer,
}));

const DetectLanguageConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./detect-language/DetectLanguageConfigRenderer'))
    .DetectLanguageConfigRenderer,
}));
const ManagedMapperConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./managed-mapper/ManagedMapperConfigRenderer'))
    .ManagedMapperConfigRenderer,
}));
const MatchCustomerWithEvidenceConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./MatchCustomerWithEvidenceConfigRenderer'))
    .MatchCustomerWithEvidenceConfigRenderer,
}));
const WebBrowserConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./web-browser/WebBrowserConfigRenderer'))
    .WebBrowserConfigRenderer,
}));
const QueryStaleCollectionThreadsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./QueryStaleCollectionThreadsConfigRenderer'))
    .QueryStaleCollectionThreadsConfigRenderer,
}));
const ScanPaymentReminderCustomersConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ScanPaymentReminderCustomersConfigRenderer'))
    .ScanPaymentReminderCustomersConfigRenderer,
}));
const ScanConversationsAwaitingReplyConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ScanConversationsAwaitingReplyConfigRenderer'))
    .ScanConversationsAwaitingReplyConfigRenderer,
}));
const ScanUnsentInvoicesConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ScanUnsentInvoicesConfigRenderer'))
    .ScanUnsentInvoicesConfigRenderer,
}));
const SummarizeCollectionThreadsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./SummarizeCollectionThreadsConfigRenderer'))
    .SummarizeCollectionThreadsConfigRenderer,
}));
const LoginToRemoteDesktopConfigRenderer = lazyRenderer(async () => ({
  default: (
    await import('./login-to-remote-desktop/LoginToRemoteDesktopConfigRenderer')
  ).LoginToRemoteDesktopConfigRenderer,
}));
const RemoteDesktopExecuteConfigRenderer = lazyRenderer(async () => ({
  default: (
    await import('./remote-desktop-execute/RemoteDesktopExecuteConfigRenderer')
  ).RemoteDesktopExecuteConfigRenderer,
}));
const FamousSqlQueryConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./FamousSqlQueryConfigRenderer'))
    .FamousSqlQueryConfigRenderer,
}));
const WriteFileToFamousConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./WriteFileToFamousConfigRenderer'))
    .WriteFileToFamousConfigRenderer,
}));
const JoinDocumentsConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./JoinDocumentsConfigRenderer'))
    .JoinDocumentsConfigRenderer,
}));
const GetProductVariantConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./GetProductVariantConfigRenderer'))
    .GetProductVariantConfigRenderer,
}));
const StartDataSourceLoadConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./StartDataSourceLoadConfigRenderer'))
    .StartDataSourceLoadConfigRenderer,
}));

/**
 * Registry mapping step types to their config renderer components
 */
const ResolveInvoiceReferencesConfigRenderer = lazyRenderer(async () => ({
  default: (await import('./ResolveInvoiceReferencesConfigRenderer'))
    .ResolveInvoiceReferencesConfigRenderer,
}));

const configRendererRegistry: Record<
  string,
  ComponentType<ConfigRendererProps>
> = {
  resolve_invoice_references: ResolveInvoiceReferencesConfigRenderer,
  ai_reasoning: AiReasoningConfigRenderer,
  verify_step_output: VerifyStepOutputConfigRenderer,
  approval: ApprovalConfigRenderer,
  submit_draft_sales_orders: SubmitDraftSalesOrdersConfigRenderer,
  event_trigger: EventTriggerConfigRenderer,
  email_rule_trigger: EmailRuleTriggerConfigRenderer,
  schedule_trigger: ScheduleTriggerConfigRenderer,
  http_trigger: HttpTriggerConfigRenderer,
  form_trigger: FormTriggerConfigRenderer,
  extract_document_content: ExtractDocumentContentConfigRenderer,
  extract_document_content_v2: ExtractDocumentContentConfigRenderer,
  extract_document_content_v3: ExtractDocumentContentConfigRenderer,
  extract_document_content_v4: ExtractDocumentContentConfigRenderer,
  schedule_payment: SchedulePaymentConfigRenderer,
  fetch_purchase_order_from_famous: FetchPurchaseOrderFromFamousConfigRenderer,
  extract_famous_order_from_email_subject:
    ExtractFamousOrderFromEmailSubjectConfigRenderer,
  classify_document: ClassifyDocumentConfigRenderer,
  process_pick_ticket_bol: ProcessPickTicketBOLConfigRenderer,
  blob_storage_fetch: BlobStorageFetchConfigRenderer,
  core_enum_fanout: CoreEnumFanoutConfigRenderer,
  parse_file: ParseFileConfigRenderer,
  parse_edi: ParseEdiConfigRenderer,
  pipeline_rows_insert: PipelineRowsInsertConfigRenderer,
  pipeline_rows_process: PipelineRowsProcessConfigRenderer,
  pipeline_rows_query: PipelineRowsQueryConfigRenderer,
  pipeline_rows_update: PipelineRowsUpdateConfigRenderer,
  background_agent: BackgroundAgentConfigRenderer,
  run_rebate_forecast: RunRebateForecastConfigRenderer,
  run_demand_forecast: RunDemandForecastConfigRenderer,
  create_task: CreateTaskConfigRenderer,
  create_typed_task: CreateTypedTaskConfigRenderer,
  generate_task: GenerateTaskConfigRenderer,
  reassign_task: ReassignTaskConfigRenderer,
  resolve_task: ResolveTaskConfigRenderer,
  create_subtask: CreateSubtaskConfigRenderer,
  complete_task: CompleteTaskConfigRenderer,
  sync_tasks_from_rows: SyncTasksFromRowsConfigRenderer,
  split_documents: SplitDocumentsConfigRenderer,
  filter_email: FilterEmailConfigRenderer,
  filter_email_by_customer: FilterEmailByCustomerConfigRenderer,
  process_sd: ProcessSDConfigRenderer,
  identify_sd_customer: IdentifySDCustomerConfigRenderer,
  identify_and_filter_sd: IdentifyAndFilterSDConfigRenderer,
  match_invoice_line_items: MatchInvoiceLineItemsConfigRenderer,
  match_supplier_document_lines: MatchSupplierDocumentLinesConfigRenderer,
  write_supplier_product_matches: WriteSupplierProductMatchesConfigRenderer,
  resolve_entity_watchers: ResolveEntityWatchersConfigRenderer,
  send_email: SendEmailConfigRenderer,
  send_announcement: SendAnnouncementConfigRenderer,
  send_announcement_v2: SendAnnouncementV2ConfigRenderer,
  notify: NotifyConfigRenderer,
  master_data_bulk_insert: MasterDataBulkInsertConfigRenderer,
  classify_master_data: ClassifyMasterDataConfigRenderer,
  transform_e844d_fields: TransformE844dFieldsConfigRenderer,
  random_number_generator: RandomNumberGeneratorConfigRenderer,
  decision: DecisionConfigRenderer,
  decision_v4: DecisionV4ConfigRenderer,
  switch: SwitchConfigRenderer,
  map_value: MapValueConfigRenderer,
  log_value: LogValueConfigRenderer,
  classify_email: ClassifyEmailConfigRenderer,
  split_excel_sheets: SplitExcelSheetsConfigRenderer,
  split_excel_sheets_v4: SplitExcelSheetsConfigRenderer,
  classify_document_v2: ClassifyDocumentContentConfigRenderer,
  entity_resolver: EntityResolverConfigRenderer,
  enqueue_rebate_session: EnqueueRebateSessionConfigRenderer,
  split_staged_po: SplitStagedPOConfigRenderer,
  split_draft_sales_order: SplitDraftSalesOrderConfigRenderer,
  emit_system_event: EmitSystemEventConfigRenderer,
  fan_in: FanInConfigRenderer,
  collect_results: CollectResultsConfigRenderer,
  classify_organization: ClassifyOrganizationConfigRenderer,
  reassign_draft_sales_order: ReassignDraftSalesOrderConfigRenderer,
  convert_e844sd_to_e844d: ConvertE844sdToE844dConfigRenderer,
  archive_ship_and_debit_azure_files:
    ArchiveShipAndDebitAzureFilesConfigRenderer,
  check_storage_write_errors: CheckStorageWriteErrorsConfigRenderer,
  platform_heartbeat: PlatformHeartbeatConfigRenderer,
  detect_as400_confirmations: DetectAs400ConfirmationsConfigRenderer,
  data_asset: DataAssetConfigRenderer,
  spa_renewal_read_as400_loads: SpaRenewalConfigRenderer,
  spa_renewal_build_load_sheet: SpaRenewalConfigRenderer,
  run_workbook: RunWorkbookConfigRenderer,
  stateful_table_write: StatefulTableWriteConfigRenderer,
  stateful_table_lookup_row: StatefulTableLookupRowConfigRenderer,
  stateful_table_read_frame: StatefulTableReadFrameConfigRenderer,
  use_data_mapping: UseDataMappingConfigRenderer,
  fusion_sync: FusionSyncConfigRenderer,
  create_jira_issue: CreateJiraIssueConfigRenderer,
  send_teams_notification: SendTeamsNotificationConfigRenderer,
  render_order_notification_email: RenderOrderNotificationEmailConfigRenderer,
  detect_order_issues: DetectOrderIssuesConfigRenderer,
  ingest_ar_data: IngestArDataConfigRenderer,
  ingest_edi_translation_lists: IngestEdiTranslationListsConfigRenderer,
  query_stale_collection_threads: QueryStaleCollectionThreadsConfigRenderer,
  scan_payment_reminder_customers: ScanPaymentReminderCustomersConfigRenderer,
  scan_conversations_awaiting_reply:
    ScanConversationsAwaitingReplyConfigRenderer,
  scan_unsent_invoices: ScanUnsentInvoicesConfigRenderer,
  summarize_collection_threads: SummarizeCollectionThreadsConfigRenderer,
  interpolate_email_template: InterpolateEmailTemplateConfigRenderer,
  split_document_v2: SplitDocumentV2ConfigRenderer,
  filter_email_attachments: FilterAttachmentsConfigRenderer,
  identify_customer: IdentifyCustomerConfigRenderer,
  identify_supplier: IdentifySupplierConfigRenderer,
  identify_location: IdentifyLocationConfigRenderer,
  workflow_action: WorkflowActionConfigRenderer,
  wait_for_event: WaitForEventConfigRenderer,
  snooze_workflow_rerun: SnoozeWorkflowRerunConfigRenderer,
  ingest_microsoft_graph_email: IngestMicrosoftGraphEmailConfigRenderer,
  create_sales_order_draft: CreateSalesOrderDraftConfigRenderer,
  create_draft_sales_order: CreateDraftSalesOrderConfigRenderer,
  resolve_draft_details: ResolveDraftDetailsConfigRenderer,
  voice_agent: VoiceAgentConfigRenderer,
  code_sandbox: CodeSandboxConfigRenderer,
  blob_storage_datasource_adapter: BlobStorageDataSourceAdapterConfigRenderer,
  // Deprecated predecessor — same renderer keeps persisted nodes configurable.
  aws_s3_datasource_adapter: BlobStorageDataSourceAdapterConfigRenderer,
  csv_transform: CsvTransformConfigRenderer,
  csv_transform_v4: CsvTransformV4ConfigRenderer,
  external_http_call: ExternalHttpCallConfigRenderer,
  watch_for_file_changes: WatchForFileChangesConfigRenderer,
  observe_data_source_link: ObserveDataSourceLinkConfigRenderer,
  upsert_sales_order_line: UpsertSalesOrderLineConfigRenderer,
  upsert_purchase_order_line: UpsertPurchaseOrderLineConfigRenderer,
  fwf_datasource_writer: FwfDataSourceWriterConfigRenderer,
  upsert_transfer_order_line: UpsertTransferOrderLineConfigRenderer,
  upsert_supplier_product: UpsertSupplierProductConfigRenderer,
  upsert_supplier_warehouse_constraint:
    UpsertSupplierWarehouseConstraintConfigRenderer,
  detect_language: DetectLanguageConfigRenderer,
  managed_mapper: ManagedMapperConfigRenderer,
  web_browser: WebBrowserConfigRenderer,
  match_customer_with_evidence: MatchCustomerWithEvidenceConfigRenderer,
  login_to_remote_desktop: LoginToRemoteDesktopConfigRenderer,
  remote_desktop_execute: RemoteDesktopExecuteConfigRenderer,
  famous_sql_query: FamousSqlQueryConfigRenderer,
  write_file_to_famous: WriteFileToFamousConfigRenderer,
  join_documents: JoinDocumentsConfigRenderer,
  get_product_variant: GetProductVariantConfigRenderer,
  start_data_source_load: StartDataSourceLoadConfigRenderer,
};

/**
 * Get the config renderer component for a given step type
 */
export function getConfigRenderer(
  stepType: string,
): ComponentType<ConfigRendererProps> {
  return configRendererRegistry[stepType] ?? StandardConfigRenderer;
}

/**
 * Warm the chunk for a step type's config renderer without rendering it.
 * Safe to call on hover/intent; a no-op for unknown or already-loaded types.
 */
export function prefetchConfigRenderer(stepType: string): void {
  const component = configRendererRegistry[stepType];
  if (!component) return;
  // Best-effort by definition: a transient chunk-fetch failure here must not
  // surface as an unhandledrejection. The render path retries on click.
  rendererLoaders
    .get(component)?.()
    .catch(() => {});
}

/**
 * Register a config renderer for a step type
 */
export function registerConfigRenderer(
  stepType: string,
  renderer: ComponentType<ConfigRendererProps>,
): void {
  configRendererRegistry[stepType] = renderer;
}

// Dynamic-step tools — a config renderer wraps its existing state setter in
// `useDynamicStepState` to expose a dynamic-config edit as an awaitable Nami
// tool (registered via `registerStepTool` while the step's panel is open).
export { useDynamicStepState } from './useDynamicStepState';
export type { DynamicStepToolOptions } from './useDynamicStepState';
export type { RegisterStepTool } from './types';
